import { useState, useEffect } from "react";
import InputDateTimePickerMUI from "../../components/InputFields/InputDatePicker/InputDateTimePickerMUI";
import SearchableDropdown from "../../components/DynamicList/SearchableDropdown";
import axios from "axios";
import { getCookie } from "../../utils/cookieHandler";
import { config } from "../../utils/config";
import {
  formatDateWithTime,
  getFirstDayOfMonthString,
  getTodayString,
} from "../../utils/helperFuctions";

const CustomerStatement = ({ id, callPopUp, userData }) => {
  const [startDate, setStartDate] = useState(getFirstDayOfMonthString());
  const [endDate, setEndDate] = useState(getTodayString());
  const [accountType, setAccountType] = useState("client"); // Default to client
  const [allBranches, setAllBranches] = useState(null);
  const [allCompanies, setAllCompanies] = useState(null); // [NEW]
  const [filteredEntities, setFilteredEntities] = useState(null); // Renamed from filteredBranches
  const [selectedEntity, setSelectedEntity] = useState(null); // Renamed from branch
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);

  // Define roles
  const adminRole = process.env.REACT_APP_ADMIN_ROLE;
  const financeRole = process.env.REACT_APP_FINANCE_ROLE;
  const clientRole = process.env.REACT_APP_CLIENT_ROLE;
  const partnerRole = process.env.REACT_APP_RENTAL_PARTNER_ROLE; // Assuming this env var exists


  useEffect(() => {
    const fetchBranches = async () => {
      const { data } = await axios.get(`${config.apiUrl}/api/branches/`, {
        headers: {
          Authorization: `Bearer ${getCookie("_AT")}`,
        },
      });
      const branches = data.data.map((branch) => ({
        value: branch.id,
        label: branch.Name,
      }));
      setAllBranches(branches);
    };


    const fetchCompanies = async () => {
      try {
        const { data } = await axios.get(`${config.apiUrl}/api/companies`, {
          headers: {
            Authorization: `Bearer ${getCookie("_AT")}`,
          },
          params: { limit: -1, fields: "id,name" }
        });
        const companies = data.data.map((comp) => ({
          value: comp.id,
          label: comp.name,
        }));
        setAllCompanies(companies);
      } catch (e) {
        console.error("Error fetching companies", e);
      }
    };

    fetchBranches();
    fetchCompanies();
  }, []);

  // Set default account type based on role
  useEffect(() => {
    if (userData?.role?.id) {
       if (userData.role.id === clientRole) {
         setAccountType("client");
       } else if (userData.role.id === partnerRole) {
         setAccountType("partner");
       }
    }
  }, [userData]);

  useEffect(() => {
    // Reset selection when account type changes
    setSelectedEntity(null);
    setFilteredEntities(null);

    if (!userData) return;

    const userRole = userData.role?.id;
    const isAdminOrFinance = userRole === adminRole || userRole === financeRole;

    if (accountType === "partner") {
      // Partner Logic (Branches)
      if (!allBranches) return;
      
      if (isAdminOrFinance) {
        setFilteredEntities(allBranches);
      } else if (userData.branches && userData.branches.length > 0) {
        // Filter by assigned branches
        const userBranchIds = userData.branches.map(b => b.branch_id?.id || b.branch_id);
        const restricted = allBranches.filter(b => userBranchIds.includes(b.value));
        setFilteredEntities(restricted);
      } else {
        setFilteredEntities([]);
      }

    } else {
      // Client Logic (Companies)
      if (!allCompanies) return;

      if (isAdminOrFinance) {
        setFilteredEntities(allCompanies);
      } else {
        // Client User: Filter by assigned company
        // Assuming userData.company exists or we need to find connection
        // Strategy: Check if userData.company (single) or userData.companies (multi) exists
        // If not found, show empty.
        
        // Try to find direct company link
        let allowedCompanyIds = [];
        if (userData.company?.id) allowedCompanyIds.push(userData.company.id);
        else if (userData.company) allowedCompanyIds.push(userData.company);
        
        // Fallback: Check if user contact has company? (Not available in simple /users/me usually unless fields requested)
        // For now, assume userData.company is populated or empty.
        
        if (allowedCompanyIds.length > 0) {
            const restricted = allCompanies.filter(c => allowedCompanyIds.includes(c.value));
            setFilteredEntities(restricted);
             if (restricted.length === 1) {
                setSelectedEntity(restricted[0].value);
             }
        } else {
             setFilteredEntities([]); 
        }
      }
    }

  }, [allBranches, allCompanies, userData, accountType]);

  return (
    <div className="w-full h-[500px] overflow-x-auto">
      <div className="flex flex-wrap gap-4 items-center">
        <div className="min-w-[180px] flex items-center h-10">
          <InputDateTimePickerMUI
            allowTime={false}
            name="start_date"
            defaultValue={startDate}
            handleChange={(e) => setStartDate(e.target.value)}
            isClearable={true}
            wrapperStyle={{ marginTop: 0 }}
          />
        </div>
        <div className="min-w-[180px] flex items-center h-10">
          <InputDateTimePickerMUI
            allowTime={false}
            name="end_date"
            defaultValue={endDate}
            handleChange={(e) => setEndDate(e.target.value)}
            isClearable={true}
            wrapperStyle={{ marginTop: 0 }}
          />
        </div>

        {(userData?.role?.id === adminRole || userData?.role?.id === financeRole) && (
              <div className="min-w-[150px] flex items-center h-10">
                <SearchableDropdown
                  label="Account Type"
                  options={[
                      { value: "client", label: "Client" },
                      { value: "partner", label: "Partner" }
                  ]}
                  value={accountType}
                  onChange={setAccountType}
                  placeholder="Select Type"
                  isClearable={false}
                />
              </div>
        )}

        <div className="min-w-[180px] flex items-center h-10">
          <SearchableDropdown
            label={accountType === "client" ? "Company" : "Branch"}
            options={filteredEntities}
            value={selectedEntity}
            onChange={setSelectedEntity}
            placeholder={accountType === "client" ? "Select Company" : "Select Branch"}
            isClearable={true}
          />
        </div>

        <button
          type="button"
          disabled={isLoading}
          className={`relative flex items-center justify-center bg-primary text-white rounded-md font-semibold transition px-4 py-[6px] text-base ${
            isLoading
              ? "opacity-70 cursor-not-allowed"
              : "hover:bg-primary-dark"
          }`}
          onClick={async (e) => {
            e.preventDefault();
            if (startDate == null) {
              callPopUp({
                title: "Start date is required",
                description: ``,
                isError: true,
                showPopUp: true,
                hideCancel: true,
              });
              return;
            }
            if (endDate == null) {
              callPopUp({
                title: `End date is required`,
                description: "",
                isError: true,
                showPopUp: true,
                hideCancel: true,
              });
              return;
            }
            if (selectedEntity == null) {
              callPopUp({
                title: `${accountType === "client" ? "Company" : "Branch"} is required`,
                description: ``,
                isError: true,
                showPopUp: true,
                hideCancel: true,
              });
              return;
            }

            setIsLoading(true);
            let url = "";
            const params = {
              startDate,
              endDate,
              branchId: accountType === "partner" ? selectedEntity : undefined,
              companyId: accountType === "client" ? selectedEntity : undefined,
              accountType
            };
            const queryParams = new URLSearchParams(params).toString();

            if (id) {
                url = `${config.apiUrl}/api/contacts/contactstatementdata/${id}?${queryParams}`;
            } else {
                url = `${config.apiUrl}/api/contacts/contactstatementdata?${queryParams}`;
            }

            const res = await axios
              .get(
                url,
                {
                  headers: {
                    Authorization: `Bearer ${getCookie("_AT")}`,
                  },
                }
              )
              .catch((e) => {
                console.log(e);
                setIsLoading(false);
              });
            setData(res.data);

            setIsLoading(false);
          }}
          style={{ fontSize: 14, height: "100%" }}
        >
          {isLoading ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="animate-spin h-5 w-5 text-white"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeOpacity="0.2"
                strokeWidth="4"
              />
              <path d="M21 12a9 9 0 1 1-6.219-8.56" />
            </svg>
          ) : (
            "Submit"
          )}
        </button>
        <div className="relative">
        <span
          className="ml-2 text-primary hover:underline hover:text-primary-dark cursor-pointer transition text-base"
            onClick={() => setShowExportMenu((v) => !v)}
          >
            Export
          </span>
          {showExportMenu && (
            <div className="absolute z-10 mt-2 w-64 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5">
              <div className="py-1 text-sm">
                <button
                  type="button"
                  className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                  onClick={async () => {
                    const response = await axios.get(`${config.apiUrl}/api/auth/pdf-token`, { headers: { Authorization: `Bearer ${getCookie("_AT")}` } });
            const tempToken = response.data.token;
            let pdfUrl = "";
            let params = `?access_token=${tempToken}&startDate=${startDate}&endDate=${endDate}&accountType=${accountType}`;
            if (accountType === "partner") params += `&branchId=${selectedEntity}`;
            if (accountType === "client") params += `&companyId=${selectedEntity}`;
            if (id) {
                 pdfUrl = `${config.apiUrl}/api/contacts/statement/${id}${params}`;
            } else {
                 pdfUrl = `${config.apiUrl}/api/contacts/statement${params}`;
            }
            window.open(`${pdfUrl}`, "_blank");
                    setShowExportMenu(false);
                  }}
                >
                  Export statement PDF
                </button>
                <button
                  type="button"
                  className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                  onClick={async () => {
                    const response = await axios.get(`${config.apiUrl}/api/auth/pdf-token`, { headers: { Authorization: `Bearer ${getCookie("_AT")}` } });
            const tempToken = response.data.token;
            let csvUrl = "";
            let params = `?access_token=${tempToken}&startDate=${startDate}&endDate=${endDate}&accountType=${accountType}`;
            if (accountType === "partner") params += `&branchId=${selectedEntity}`;
            if (accountType === "client") params += `&companyId=${selectedEntity}`;
            if (id) {
                csvUrl = `${config.apiUrl}/api/contacts/contactstatementcsv/${id}${params}`;
            } else {
                csvUrl = `${config.apiUrl}/api/contacts/contactstatementcsv${params}`;
            }
                    const csvResponse = await axios.get(csvUrl, { responseType: "blob", headers: { Authorization: `Bearer ${getCookie("_AT")}` } });
            const blob = new Blob([csvResponse.data], { type: "text/csv" });
            const link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.download = `Customer_statement_${id}.csv`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            setTimeout(() => window.URL.revokeObjectURL(link.href), 500);
                    setShowExportMenu(false);
                  }}
                >
                  Export statement CSV
                </button>
                <button
                  type="button"
                  className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                  onClick={async () => {
                    const response = await axios.get(`${config.apiUrl}/api/auth/pdf-token`, { headers: { Authorization: `Bearer ${getCookie("_AT")}` } });
                    const tempToken = response.data.token;
                    let pdfUrl = "";
                    let params = `?access_token=${tempToken}&startDate=${startDate}&endDate=${endDate}&accountType=${accountType}`;
                    if (accountType === "partner") params += `&branchId=${selectedEntity}`;
                    if (accountType === "client") params += `&companyId=${selectedEntity}`;
                    if (id) {
                        pdfUrl = `${config.apiUrl}/api/contacts/summary/${id}${params}`;
                    } else {
                        pdfUrl = `${config.apiUrl}/api/contacts/summary${params}`;
                    }
                    window.open(`${pdfUrl}`, "_blank");
                    setShowExportMenu(false);
                  }}
                >
                  Export summary PDF
                </button>
                <button
                  type="button"
                  className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                  onClick={async () => {
                    const response = await axios.get(`${config.apiUrl}/api/auth/pdf-token`, { headers: { Authorization: `Bearer ${getCookie("_AT")}` } });
                    const tempToken = response.data.token;
                    let csvUrl = "";
                    let params = `?access_token=${tempToken}&startDate=${startDate}&endDate=${endDate}&accountType=${accountType}`;
                    if (accountType === "partner") params += `&branchId=${selectedEntity}`;
                    if (accountType === "client") params += `&companyId=${selectedEntity}`;
                    if (id) {
                        csvUrl = `${config.apiUrl}/api/contacts/contactsummarycsv/${id}${params}`;
                    } else {
                        csvUrl = `${config.apiUrl}/api/contacts/contactsummarycsv${params}`;
                    }
                    const csvResponse = await axios.get(csvUrl, { responseType: "blob", headers: { Authorization: `Bearer ${getCookie("_AT")}` } });
                    const blob = new Blob([csvResponse.data], { type: "text/csv" });
                    const link = document.createElement("a");
                    link.href = window.URL.createObjectURL(blob);
                    link.download = `Customer_summary_${id}.csv`;
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    setTimeout(() => window.URL.revokeObjectURL(link.href), 500);
                    setShowExportMenu(false);
                  }}
                >
                  Export summary CSV
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <table className="min-w-full divide-y divide-gray-300 mt-4 text-[12px] ">
        <thead>
          <tr>
            <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0">
              Date
            </th>
            <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
              ID
            </th>
            {!id && (
              <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
                Customer
              </th>
            )}
            <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
              Description
            </th>
            <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">
              Vehicle
            </th>
            <th className="py-3.5 px-3 text-right text-sm font-semibold text-gray-900">
              Amount
            </th>
            <th className="py-3.5 px-3 text-right text-sm font-semibold text-gray-900">
              Main Balance
            </th>
            <th className="py-3.5 pr-4 text-right text-sm font-semibold text-gray-900 sm:pr-0">
              Deposit Balance
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {/* Brought Forward */}
          {data != null &&
            data.showBroughtForward != null &&
            data.showBroughtForward === true && (
              <tr className="bg-[#f7f7fa]">
                <td className="py-4 pl-4 pr-3 sm:pl-0">
                  {data?.broughtForwardDate && (
                    <span>{data.broughtForwardDate}</span>
                  )}
                </td>
                <td className="px-3"></td>
                {!id && <td className="px-3"></td>}
                <td className="px-3 font-semibold">Brought Forward</td>
                <td className="px-3"></td>
                <td className="px-3 text-right font-semibold"></td>
                <td className="px-3 text-right font-semibold">
                  {data?.currency?.code} {data?.broughtForwardBalance ?? "0"}
                </td>
                <td className="pr-4 text-right font-semibold sm:pr-0">
                  {data?.currency?.code} {data?.broughtForwardDeposit ?? "0"}
                </td>
              </tr>
            )}
          {/* Statement Items */}
          {data?.items?.length > 0 &&
            data.items.map((item, i) => (
              <tr key={i}>
                <td className="py-4 pl-4 pr-3 sm:pl-0">{item.date}</td>
                <td className="px-3">{item.reservationId}</td>
                {!id && <td className="px-3">{item.contactName}</td>}
                <td className="px-3">
                  {item.description}
                </td>
                <td className="px-3">{item.car}</td>
                <td className="px-3 text-right">
                  {data.currency.code}{" "}
                  {item.amount !== null && item.amount !== undefined
                    ? item.amount_display
                    : "0"}
                </td>
                <td className="px-3 text-right font-semibold">
                  {data.currency.code} {item.main_balance}
                </td>
                <td className="pr-4 text-right font-semibold sm:pr-0">
                  {item.deposit_balance === "-"
                    ? "-"
                    : `${data.currency.code} ${item.deposit_balance}`}
                </td>
              </tr>
            ))}
          {/* Totals */}
          {(data?.total_balance || data?.total_deposit) && (
            <tr className="bg-[#f7f7fa]">
              <td colSpan={!id ? 5 : 4}></td>
              <td className="px-3 py-4 text-right font-bold">Total</td>
              <td className="px-3 py-4 text-right font-bold">
                {data.currency.code} {data.total_balance}
              </td>
              <td className="pr-4 text-right font-bold sm:pr-0">
                {data.currency.code} {data.total_deposit}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default CustomerStatement;
